var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx } from "react/jsx-runtime";
// Copyright 2022 The Parca Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import { useEffect, useState } from 'react';
import { QueryRequest_ReportType } from '@parca/client';
import { useGrpcMetadata, useParcaContext, useURLState } from '@parca/components';
import { saveAsBlob } from '@parca/functions';
import useUserPreference, { USER_PREFERENCES } from '@parca/functions/useUserPreference';
import { ProfileView } from './ProfileView';
import { useQuery } from './useQuery';
import { downloadPprof } from './utils';
export var ProfileViewWithData = function (_a) {
    var _b, _c;
    var queryClient = _a.queryClient, profileSource = _a.profileSource, navigateTo = _a.navigateTo;
    var metadata = useGrpcMetadata();
    var dashboardItems = useURLState({ param: 'dashboard_items', navigateTo: navigateTo })[0];
    var _d = useState(0), nodeTrimThreshold = _d[0], setNodeTrimThreshold = _d[1];
    var enableTrimming = useUserPreference(USER_PREFERENCES.ENABLE_GRAPH_TRIMMING.key)[0];
    useEffect(function () {
        if (!enableTrimming) {
            setNodeTrimThreshold(0);
        }
    }, [enableTrimming]);
    var onFlamegraphContainerResize = function (width) {
        if (!enableTrimming || width === 0) {
            return;
        }
        var threshold = (1 / width) * 100;
        if (threshold === nodeTrimThreshold) {
            return;
        }
        setNodeTrimThreshold(threshold);
    };
    var _e = useQuery(queryClient, profileSource, QueryRequest_ReportType.FLAMEGRAPH_TABLE, {
        skip: !dashboardItems.includes('icicle'),
        nodeTrimThreshold: nodeTrimThreshold,
    }), flamegraphLoading = _e.isLoading, flamegraphResponse = _e.response, flamegraphError = _e.error;
    var perf = useParcaContext().perf;
    var _f = useQuery(queryClient, profileSource, QueryRequest_ReportType.TOP, {
        skip: !dashboardItems.includes('table'),
    }), topTableLoading = _f.isLoading, topTableResponse = _f.response, topTableError = _f.error;
    var _g = useQuery(queryClient, profileSource, QueryRequest_ReportType.CALLGRAPH, {
        skip: !dashboardItems.includes('callgraph'),
    }), callgraphLoading = _g.isLoading, callgraphResponse = _g.response, callgraphError = _g.error;
    useEffect(function () {
        var _a, _b;
        if (!flamegraphLoading && (flamegraphResponse === null || flamegraphResponse === void 0 ? void 0 : flamegraphResponse.report.oneofKind) === 'flamegraph') {
            perf === null || perf === void 0 ? void 0 : perf.markInteraction('Flamegraph render', flamegraphResponse.report.flamegraph.total);
        }
        if (!topTableLoading && (topTableResponse === null || topTableResponse === void 0 ? void 0 : topTableResponse.report.oneofKind) === 'top') {
            perf === null || perf === void 0 ? void 0 : perf.markInteraction('Top table render', (_a = topTableResponse === null || topTableResponse === void 0 ? void 0 : topTableResponse.report) === null || _a === void 0 ? void 0 : _a.top.total);
        }
        if (!callgraphLoading && (callgraphResponse === null || callgraphResponse === void 0 ? void 0 : callgraphResponse.report.oneofKind) === 'callgraph') {
            perf === null || perf === void 0 ? void 0 : perf.markInteraction('Callgraph render', (_b = callgraphResponse === null || callgraphResponse === void 0 ? void 0 : callgraphResponse.report) === null || _b === void 0 ? void 0 : _b.callgraph.cumulative);
        }
    }, [
        flamegraphLoading,
        flamegraphResponse,
        callgraphResponse,
        callgraphLoading,
        topTableLoading,
        topTableResponse,
        perf,
    ]);
    var sampleUnit = profileSource.ProfileType().sampleUnit;
    var downloadPProfClick = function () { return __awaiter(void 0, void 0, void 0, function () {
        var blob, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (profileSource == null || queryClient == null) {
                        return [2 /*return*/];
                    }
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, downloadPprof(profileSource.QueryRequest(), queryClient, metadata)];
                case 2:
                    blob = _a.sent();
                    saveAsBlob(blob, "profile.pb.gz");
                    return [3 /*break*/, 4];
                case 3:
                    error_1 = _a.sent();
                    console.error('Error while querying', error_1);
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    return (_jsx(ProfileView, { flamegraphData: {
            loading: flamegraphLoading,
            data: (flamegraphResponse === null || flamegraphResponse === void 0 ? void 0 : flamegraphResponse.report.oneofKind) === 'flamegraph'
                ? (_b = flamegraphResponse === null || flamegraphResponse === void 0 ? void 0 : flamegraphResponse.report) === null || _b === void 0 ? void 0 : _b.flamegraph
                : undefined,
            error: flamegraphError,
        }, topTableData: {
            loading: topTableLoading,
            data: (topTableResponse === null || topTableResponse === void 0 ? void 0 : topTableResponse.report.oneofKind) === 'top' ? topTableResponse.report.top : undefined,
            error: topTableError,
        }, callgraphData: {
            loading: callgraphLoading,
            data: (callgraphResponse === null || callgraphResponse === void 0 ? void 0 : callgraphResponse.report.oneofKind) === 'callgraph'
                ? (_c = callgraphResponse === null || callgraphResponse === void 0 ? void 0 : callgraphResponse.report) === null || _c === void 0 ? void 0 : _c.callgraph
                : undefined,
            error: callgraphError,
        }, sampleUnit: sampleUnit, profileSource: profileSource, queryClient: queryClient, navigateTo: navigateTo, onDownloadPProf: function () { return void downloadPProfClick(); }, onFlamegraphContainerResize: onFlamegraphContainerResize }));
};
export default ProfileViewWithData;
